import {
  AriaDatePickerProps,
  AriaTimeFieldProps,
  DateValue,
  TimeValue,
  useDateField,
  useDateSegment,
  useLocale,
  useTimeField,
} from 'react-aria'
import {
  DateFieldState,
  DateSegment as RADateSegment,
  useDateFieldState,
  useTimeFieldState,
} from 'react-stately'
import { useRef } from 'react'
import { createCalendar } from '@internationalized/date'
import styled from 'styled-components'

const Container = styled.div`
  width: 180px; // Remove this when date time is added.
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`

const Field = styled.div`
  width: 100%;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  background-color: ${({ theme }) => theme.colors.surface.subdued};
  color: ${({ theme }) => theme.colors.font.regular};
  padding: 0.625em;
  border: 1px solid ${({ theme }) => theme.colors.button.secondaryBorder};
  border-radius: 4px;

  ::placeholder {
    color: ${({ theme }) => theme.colors.font.placeholder};
  }

  &:hover,
  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.focused};
  }

  &:active {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.selected};
  }

  &:focus-within {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.focused};
    border-radius: 4px;
  }
`

const SegmentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25em;
`

type Props = AriaDatePickerProps<DateValue>

export const DateField: React.FC<Props> = (props) => {
  const { locale } = useLocale()
  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  })

  const ref = useRef(null)
  const { fieldProps } = useDateField(props, state, ref)

  return (
    <Container>
      <Field {...fieldProps} ref={ref}>
        <SegmentContainer>
          {state.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={state} />
          ))}
        </SegmentContainer>
      </Field>
    </Container>
  )
}

const Segment = styled.div`
  padding: 0 3px;
  text-align: end;

  ::placeholder {
    color: ${({ theme }) => theme.colors.font.placeholder};
  }

  &:focus {
    border: 1px solid ${({ theme }) => theme.colors.system.interactive.focused};
    outline: none;
    border-radius: 3px;
  }
`
interface SegmentProps {
  segment: RADateSegment
  state: DateFieldState
}

const DateSegment: React.FC<SegmentProps> = ({ segment, state }) => {
  const ref = useRef(null)
  const { segmentProps } = useDateSegment(segment, state, ref)
  return (
    <Segment {...segmentProps} ref={ref}>
      {segment.text}
    </Segment>
  )
}

const StyledTimeField = styled(Field)`
  background-color: white;
`
type TimeFieldProps = AriaTimeFieldProps<TimeValue>

export const TimeField: React.FC<TimeFieldProps> = (props) => {
  const { locale } = useLocale()
  const state = useTimeFieldState({
    ...props,
    locale,
  })

  const ref = useRef(null)
  const { labelProps, fieldProps } = useTimeField(props, state, ref)

  return (
    <Container>
      <label {...labelProps}>{props.label}</label>
      <StyledTimeField {...fieldProps} ref={ref}>
        <SegmentContainer>
          {state.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={state} />
          ))}
        </SegmentContainer>
      </StyledTimeField>
    </Container>
  )
}
